"use client";

import { X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "../lib/hooks/use-mobile";
import { BottomSheetDrawer } from "./bottom-sheet-drawer";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

export type ResponsiveDialogProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
  header?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  showClose?: boolean;
  dismissible?: boolean;
  // Mobile only: keeps the bottom sheet at the full viewport height so its size
  // does not follow the content. Ignored by the desktop dialog.
  fullHeight?: boolean;
  modal?: boolean;
  closeLabel?: string;
  className?: string;
  dialogClassName?: string;
  drawerClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  overlayClassName?: string;
  closeClassName?: string;
  closeIcon?: ReactNode;
  titleClassName?: string;
  descriptionClassName?: string;
};

export const ResponsiveDialog = ({
  open,
  onOpenChange,
  trigger,
  header,
  title,
  description,
  children,
  footer,
  showClose = true,
  dismissible = true,
  fullHeight = false,
  modal = true,
  closeLabel = "Close",
  className,
  dialogClassName,
  drawerClassName,
  contentClassName,
  headerClassName,
  footerClassName,
  overlayClassName,
  closeClassName,
  closeIcon,
  titleClassName,
  descriptionClassName,
}: ResponsiveDialogProps) => {
  const isMobile = useIsMobile();
  const dialogHeaderContent = header ? (
    <div className={cn(showClose ? "pr-10" : undefined, headerClassName)}>
      {header}
    </div>
  ) : title || description ? (
    <DialogHeader
      className={cn(
        showClose ? "pr-10" : undefined,
        "text-left",
        headerClassName,
      )}
    >
      {title ? (
        <DialogTitle className={titleClassName}>{title}</DialogTitle>
      ) : null}
      {description ? (
        <DialogDescription className={descriptionClassName}>
          {description}
        </DialogDescription>
      ) : null}
    </DialogHeader>
  ) : null;

  if (isMobile) {
    return (
      <BottomSheetDrawer
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        header={header}
        title={title}
        description={description}
        content={<div className={contentClassName}>{children}</div>}
        footer={footer}
        showClose={showClose}
        dismissible={dismissible}
        fullHeight={fullHeight}
        closeLabel={closeLabel}
        className={cn(className, drawerClassName)}
        headerClassName={headerClassName}
        footerClassName={footerClassName}
        closeClassName={closeClassName}
        closeIcon={closeIcon}
        titleClassName={titleClassName}
        descriptionClassName={descriptionClassName}
      />
    );
  }

  return (
    <Dialog modal={modal} onOpenChange={onOpenChange} open={open}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        className={cn(className, dialogClassName)}
        closeButton={false}
        overlayClassName={overlayClassName}
        onEscapeKeyDown={(event) => {
          if (!dismissible) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (!dismissible) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (!dismissible) {
            event.preventDefault();
          }
        }}
      >
        {(dialogHeaderContent || showClose) && (
          <div className="relative">
            {dialogHeaderContent}
            {showClose ? (
              <DialogClose
                className={cn(
                  "absolute top-1/2 right-6 -translate-y-1/2 rounded-xs opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none",
                  closeClassName,
                )}
              >
                {closeIcon ?? <X className="size-6" />}
                <span className="sr-only">{closeLabel}</span>
              </DialogClose>
            ) : null}
          </div>
        )}
        <div className={contentClassName}>{children}</div>
        {footer ? (
          <DialogFooter className={footerClassName}>{footer}</DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
